import { useEffect } from "react";
import {
  TradeForm,
  TradePathData,
  WidgetEventConnectSpecificExchangePayload,
  WidgetEventMessage,
  WidgetEventUpdateExchangePayload,
  WidgetEventUpdatePairPayload,
  WidgetWindowEvents,
} from "@cede/types";

type TradeDispatcherParams = {
  enabled: boolean;
  tradeForm: TradeForm;
  tradePath: TradePathData[] | null | undefined;
};

export const connectSpecificExchangeAccount = (exchangeId: string) => {
  const message: WidgetEventMessage<WidgetEventConnectSpecificExchangePayload> = {
    type: WidgetWindowEvents.CONNECT_SPECIFIC_EXCHANGE,
    payload: { exchangeId },
  };
  window.postMessage(message);
};

export const connectExchangeAccount = () => {
  const message: WidgetEventMessage<null> = {
    type: WidgetWindowEvents.CONNECT_EXCHANGE,
    payload: null,
  };
  window.postMessage(message);
};

/**
 * Dispatch events through the window object (only available for Widgets)
 */
export const useTradeEventDispatcher = ({ enabled, tradeForm, tradePath }: TradeDispatcherParams) => {
  const onChangeSelectedAccount = () => {
    const message: WidgetEventMessage<WidgetEventUpdateExchangePayload> = {
      type: WidgetWindowEvents.UPDATE_EXCHANGE,
      payload: tradeForm.selectedAccount
        ? { exchangeId: tradeForm.selectedAccount?.cexValue, accountId: tradeForm.selectedAccount?.value }
        : null,
    };
    window.postMessage(message);
  };

  const onChangePairSymbol = async () => {
    let pairSymbol = "";
    let precision = 0;
    let isTradeSquare = false;
    if (tradeForm.market) {
      pairSymbol = tradeForm.market.pairSymbol;
      precision = tradeForm.market.precision?.price ?? 0;
    } else if (tradePath?.length) {
      const lastTradePath = tradePath[tradePath.length - 1];
      if (!lastTradePath) return;
      // we want to use the precision of the destination token
      // eg. BTC/USDT: price precision is 0.01 but if the destination token is BTC,
      // we want to use the BTC (base amount) precision which is 0.00000001
      precision = lastTradePath.orderSide === "sell" ? lastTradePath.precision.price : lastTradePath.precision.amount;
      pairSymbol = `${tradeForm.tokenFrom?.value}/${tradeForm.tokenTo?.value}`;
      isTradeSquare = true;
    }

    const message: WidgetEventMessage<WidgetEventUpdatePairPayload> = {
      type: WidgetWindowEvents.UPDATE_PAIR,
      payload: pairSymbol
        ? {
            pairSymbol,
            precision,
            isTradeSquare,
            // Include tradePath for multi-route trades (used by TradingView tabs)
            tradePath: isTradeSquare && tradePath?.length ? tradePath : undefined,
          }
        : null,
    };
    window.postMessage(message);
  };

  useEffect(() => {
    if (!enabled) return;
    onChangeSelectedAccount();
  }, [tradeForm.selectedAccount, enabled]);

  useEffect(() => {
    if (!enabled) return;
    onChangePairSymbol();
  }, [tradeForm.market, tradePath, enabled]);
};
